import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  getCurrentUser,
  signOut,
  fetchAuthSession,
  fetchMFAPreference,
  confirmSignIn,
  AuthUser,
  signIn as cognitoSignIn,
  signInWithRedirect,
  AuthSession,
  SignInOutput,
  ConfirmSignInOutput,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import User from '../services/User';
import UserSettingsUtils from '../utils/UserSettingsUtils';
import { useTheme } from './Theme';
import awsConfig from '../aws-config';

type SignInReturn = { success: boolean; nextStep?: string };
export type SignInInput = { email: string; password: string; newPassword?: string };

type AuthCtx = {
  refactoredUser?: User;
  signed: boolean;
  user?: AuthUser;
  session?: AuthSession;
  loading: boolean;
  signIn: (credentials: SignInInput) => Promise<SignInOutput>;
  signInExternal: (provider: string) => Promise<void>;
  respondWithTotp: (code: string) => Promise<SignInReturn>;
  confirmSignInWithPassword: (
    username: string,
    tempPassword: string,
    newPassword: string
  ) => Promise<ConfirmSignInOutput>;
  logout: () => void;
  federatedLogout: () => void;
};

Amplify.configure(awsConfig);

const AuthContext = createContext<AuthCtx | null>(null);

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const { setTheme } = useTheme() as any;
  const [user, setUser] = useState<AuthUser>();
  const [refactoredUser, setRefactoredUser] = useState<User>();
  const [session, setSession] = useState<AuthSession>();
  const initSession = async () => {
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const user = await getCurrentUser();
      const mfaSettings = await fetchMFAPreference();

      setRefactoredUser(new User(session, mfaSettings.enabled?.includes('TOTP') ?? false));
      setUser(user);
      setSession(session);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const afterLogout = useCallback(async () => {
    localStorage.clear();
    localStorage.setItem('logout', 'true');
    UserSettingsUtils.clearSettings();
    setUser(undefined);
    setSession(undefined);
    setTheme('light');
    setRefactoredUser(undefined);
  }, [setTheme]);

  const logout = useCallback(async () => {
    setLoading(true);
    await signOut();
  }, []);

  const federatedLogout = useCallback(async () => {
    localStorage.clear();
    localStorage.setItem('logout', 'true');
    UserSettingsUtils.clearSettings();
    setUser(undefined);
    setSession(undefined);
    setTheme('light');
    setRefactoredUser(undefined);

    const cognitoDomain = process.env.REACT_APP_OAUTH_DOMAIN || 'csdevmi.auth.us-east-1.amazoncognito.com';
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = encodeURIComponent(`${window.location.origin}/auth/login`);

    if (cognitoDomain && clientId) {
      window.location.href = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
  }, [setTheme]);

  useEffect(() => {
    return Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signedOut') {
        afterLogout();
        if (!refactoredUser?.isFederated()) {
          setLoading(false);
        }
      }
    });
  }, [afterLogout, refactoredUser]);

  const respondWithTotp = useCallback(async (code: string) => {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

    if (isSignedIn) {
      initSession();
    }

    return { success: isSignedIn };
  }, []);

  const signInExternal = useCallback((provider: string) => {
    return signInWithRedirect({ provider: { custom: provider } });
  }, []);

  const signIn = useCallback(async (credentials: SignInInput) => {
    await signOut();

    const response = await cognitoSignIn({
      username: credentials.email,
      password: credentials.password,
    });
    const { nextStep } = response;

    switch (nextStep.signInStep) {
      case 'DONE': {
        initSession();
        break;
      }
    }

    return response;
  }, []);

  const confirmSignInWithPassword = useCallback(
    async (username: string, tempPassword: string, newPassword: string) => {
      const { nextStep } = await signIn({ email: username, password: tempPassword });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const response = await confirmSignIn({
          challengeResponse: newPassword,
        });
        const { isSignedIn } = response;

        if (isSignedIn) {
          return response;
        } else {
          throw new Error('Username or temporary password is incorrect');
        }
      } else {
        throw new Error('Incorrect sign in step');
      }
    },
    []
  );

  // TODO: EA token bridge — embedding energyarray-home inside energyarray.net via iframe.
  // Plan put on hold. Re-enable and test end-to-end when the plan resumes.
  useEffect(() => {
    initSession();
  }, []);

  // useEffect(() => {
  //   if (window.self !== window.top) {
  //     // Running inside an EA iframe — wait for token bridge postMessage.
  //     // Fall back to normal init after 3 s in case the message never arrives.
  //     bridgeTimeoutRef.current = setTimeout(() => initSession(), 3000);
  //   } else {
  //     initSession();
  //   }
  //   return () => {
  //     if (bridgeTimeoutRef.current) clearTimeout(bridgeTimeoutRef.current);
  //   };
  // }, []);
  //
  // // Receive tokens injected by the EA shell when running inside an iframe.
  // useEffect(() => {
  //   const EA_ORIGINS = [
  //     'https://stage.energyarray.net',
  //     'https://www.energyarray.net',
  //   ];
  //
  //   const handleEaTokens = async (event: MessageEvent) => {
  //     if (!EA_ORIGINS.includes(event.origin)) return;
  //     if (event.data?.type !== 'EA_AUTH_TOKENS') return;
  //
  //     const { accessToken, idToken, refreshToken } = event.data;
  //     if (!accessToken || !idToken) return;
  //
  //     if (bridgeTimeoutRef.current) {
  //       clearTimeout(bridgeTimeoutRef.current);
  //       bridgeTimeoutRef.current = null;
  //     }
  //
  //     try {
  //       const payload = JSON.parse(atob(accessToken.split('.')[1]));
  //       const username: string = payload.username || payload.sub;
  //       const clientId = String(process.env.REACT_APP_COGNITO_CLIENT_ID);
  //
  //       localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
  //       localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, accessToken);
  //       localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, idToken);
  //       if (refreshToken) {
  //         localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, refreshToken);
  //       }
  //       localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.clockDrift`, '0');
  //
  //       await initSession();
  //     } catch (err) {
  //       console.error('[EA token bridge] Failed to inject tokens:', err);
  //     }
  //   };
  //
  //   window.addEventListener('message', handleEaTokens);
  //
  //   // Handshake: notify EA shell that we're ready to receive tokens.
  //   if (window.self !== window.top) {
  //     window.parent.postMessage({ type: 'EA_TOKEN_REQUEST' }, '*');
  //   }
  //
  //   return () => window.removeEventListener('message', handleEaTokens);
  // }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        signed: !!session,
        refactoredUser,
        loading,
        signInExternal,
        signIn,
        respondWithTotp,
        confirmSignInWithPassword,
        logout,
        federatedLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext) as AuthCtx;

export default AuthProvider;